// Visual effects for attacks, spells and conditions (issue 1164): a short animation on the board, then nothing.
//
// Client-only, driven by the frames this viewer is already sent, so an effect never shows more than the frame does:
// the DM's whole attack and resolve rolls (ids, the area), a player's `playerAttack`/`playerSave` versions (names and
// the outcome, never an id), and the store's own objects and areas. A player's effect is placed only where this
// browser can say where without guessing: the attack or resolve it sent itself (it knows the ids it sent), or the one
// token / area in its own store bearing the name the frame gives. Unnamed, ambiguous, hidden, on another scene: nothing.
// Each effect lasts at most `MAX_MS`; the board draws them on a layer of their own, per frame only while one plays.
import {
  areaSpans, attackOutcome, saveOutcome, type Area, type AttackResult, type EventBody, type Grid, type PlayerAttack, type PlayerSave, type ProjectedEvent, type ProjectedState, type RollRecord,
} from '@dragons-table/vtt-shared';
import { boardSquares, spanRects } from './areaGeometry';

export interface Pt { x: number; y: number }
export type Effect =
  /** An attack: a streak from the attacker (null: none shown) to the target, then a hit flash or a miss puff there. */
  | { kind: 'attack'; from: Pt | null; to: Pt; result: AttackResult }
  /** A template resolved: an expanding fill over exactly the area's squares, fading. */
  | { kind: 'burst'; area: Area }
  /** A condition gained: a small pulse on the token. */
  | { kind: 'pulse'; at: Pt };

export const EFFECT_MS: Record<Effect['kind'], number> = { attack: 900, burst: 1100, pulse: 700 };
export const MAX_MS = 1200;

// ── The setting ───────────────────────────────────────────────────────────

const KEY = 'vtt.effects';
const reducedMotion = (): boolean => { try { return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; } };
const settingListeners = new Set<(on: boolean) => void>();
/** Each person's own switch, kept in this browser: on by default, off by default where the system asks for less motion. */
export const effectsSetting = {
  get(): boolean {
    try { const v = localStorage.getItem(KEY); if (v === '1') return true; if (v === '0') return false; } catch { /* storage refused: the default */ }
    return !reducedMotion();
  },
  set(on: boolean): void {
    try { localStorage.setItem(KEY, on ? '1' : '0'); } catch { /* storage refused: this page only */ }
    for (const l of settingListeners) l(on);
  },
  subscribe(l: (on: boolean) => void): () => void { settingListeners.add(l); return () => { settingListeners.delete(l); }; },
};

// ── What this browser sent (a player's own attack or resolve, located by its own ids) ──

type Sent = { kind: 'attack'; attacker: string; target: string; at: number } | { kind: 'save'; areaId: string; at: number };
const SENT_MS = 15_000;
let sent: Sent[] = [];
/** Called for every act this browser sends: an attack's two tokens and a resolve's area are remembered briefly. */
export function noteSent(body: EventBody, now = Date.now()): void {
  sent = sent.filter((s) => now - s.at < SENT_MS);
  const b = body as { kind: string; attacker?: unknown; target?: unknown; areaId?: unknown };
  if (b.kind === 'attack.request' && typeof b.attacker === 'string' && typeof b.target === 'string') sent.push({ kind: 'attack', attacker: b.attacker, target: b.target, at: now });
  else if (b.kind === 'area.resolve' && typeof b.areaId === 'string') sent.push({ kind: 'save', areaId: b.areaId, at: now });
}
const takeSent = <K extends Sent['kind']>(kind: K, now: number): Extract<Sent, { kind: K }> | null => {
  sent = sent.filter((s) => now - s.at < SENT_MS);
  const k = sent.findIndex((s) => s.kind === kind);
  if (k < 0) return null;
  const [s] = sent.splice(k, 1);
  return s as Extract<Sent, { kind: K }>;
};
/** For tests: forget what was sent and what has played. */
export function resetEffects(): void { sent = []; played.clear(); }

// ── From a frame to effects ──────────────────────────────────────────────

/**
 * What each roll has played (issue 1438). A roll's frame comes again with the same id when it is amended, when the DM
 * supplies an attack's defence or a creature's save bonus: its swing never plays twice, and its outcome plays once,
 * when it becomes known. An attack still waiting for the DM has played its streak alone and remembers where its
 * outcome will show; a template whose saves still wait remembers its area, and fills it once they are all known.
 */
type Waiting = { kind: 'attack'; target: string | null; name: string | null } | { kind: 'save'; area: string | null };
const played = new Map<string, Waiting | 'done'>();
const remember = (id: string, v: Waiting | 'done'): void => {
  played.delete(id); played.set(id, v);
  if (played.size > 500) played.delete(played.keys().next().value as string);
};
const at = (state: ProjectedState, id: string | null | undefined): Pt | null => {
  const o = id ? state.objects[id] : undefined;
  return o ? { x: o.x, y: o.y } : null;
};
/** The one token in this store wearing `name`, or null (no name, none, or more than one). */
const byName = (state: ProjectedState, name: string | null | undefined): Pt | null => {
  if (!name) return null;
  const hits = Object.values(state.objects).filter((o) => (o as { name?: string }).name === name);
  return hits.length === 1 ? { x: hits[0]!.x, y: hits[0]!.y } : null;
};
const areaNamed = (state: ProjectedState, name: string | null): Area | null => {
  if (!name) return null;
  const hits = Object.values(state.areas ?? {}).filter((a) => a.label === name);
  return hits.length === 1 ? hits[0]! : null;
};

/** Where an attack's outcome shows: the target by its id when this browser knows it, else the one token of its name. */
const targetAt = (state: ProjectedState, w: { target: string | null; name: string | null }): Pt | null => (w.target ? at(state, w.target) : null) ?? byName(state, w.name);

/** An attack's frame: the whole swing the first time, its outcome alone when the DM's defence later decides it. */
function attackFx(id: string, result: AttackResult, place: () => { from: Pt | null; to: Pt | null; target: string | null; name: string | null }, state: ProjectedState): Effect[] {
  const was = played.get(id);
  if (was === 'done') return [];
  if (was) {
    if (result === 'unresolved' || was.kind !== 'attack') return [];
    remember(id, 'done');
    const to = targetAt(state, was);
    return to ? [{ kind: 'attack', from: null, to, result }] : [];
  }
  const p = place();
  remember(id, result === 'unresolved' ? { kind: 'attack', target: p.target, name: p.name } : 'done');
  return p.to ? [{ kind: 'attack', from: p.from, to: p.to, result }] : [];
}
/** A template's frame: its fill once every save this viewer is told of is known; until then it waits. */
function saveFx(id: string, waiting: boolean, area: () => string | null, state: ProjectedState): Effect[] {
  const was = played.get(id);
  if (was === 'done') return [];
  const areaId = was && was.kind === 'save' ? was.area : was ? null : area();
  if (waiting) { if (!was) remember(id, { kind: 'save', area: areaId }); return []; }
  remember(id, 'done');
  const a = areaId ? state.areas?.[areaId] : undefined;
  return a ? [{ kind: 'burst', area: a }] : [];
}

function dmAttack(roll: RollRecord, state: ProjectedState): Effect[] {
  const f = roll.attack!;
  return attackFx(roll.id, attackOutcome(roll, f).result, () => ({ from: at(state, f.attacker.id), to: at(state, f.target.id), target: f.target.id, name: null }), state);
}
function dmSave(roll: RollRecord, state: ProjectedState): Effect[] {
  const f = roll.save!;
  const waiting = (f.targets ?? []).some((t) => saveOutcome(t, f).result === 'unresolved');
  return saveFx(roll.id, waiting, () => f.area, state);
}
function playerAttackFx(a: PlayerAttack, state: ProjectedState, meId: string, now: number): Effect[] {
  return attackFx(a.id, a.result, () => {
    const mine = a.actor.id === meId ? takeSent('attack', now) : null;
    const target = mine && at(state, mine.target) ? mine.target : null;
    const to = (target ? at(state, target) : null) ?? byName(state, a.target.name);
    // The attacker is drawn only when the frame says this player sees it; unnamed or unknown, the effect is at the target alone.
    const from = to && a.attacker ? ((mine && at(state, mine.attacker)) ?? byName(state, a.attacker.name)) : null;
    return { from, to, target, name: a.target.name };
  }, state);
}
function playerSaveFx(s: PlayerSave, state: ProjectedState, meId: string, now: number): Effect[] {
  return saveFx(s.id, s.targets.some((t) => t.result === 'unresolved'), () => {
    const mine = s.actor.id === meId ? takeSent('save', now) : null;
    if (s.area && state.areas?.[s.area]) return s.area; // the template it names (issue 1438), whoever laid it
    return (mine && state.areas?.[mine.areaId] ? mine.areaId : null) ?? areaNamed(state, s.name)?.id ?? null;
  }, state);
}
/** A condition a token gained between two stores: a pulse on it. Only labels this viewer's store holds count. */
function gained(before: ProjectedState, after: ProjectedState): Effect[] {
  if (before.objects === after.objects) return [];
  const out: Effect[] = [];
  for (const o of Object.values(after.objects)) {
    const was = before.objects[o.id];
    const now = o.values?.conditions ?? [];
    if (!was || !now.length) continue;
    const had = new Set(was.values?.conditions ?? []);
    if (now.some((c) => !had.has(c))) out.push({ kind: 'pulse', at: { x: o.x, y: o.y } });
  }
  return out;
}

/**
 * The effects one frame starts for this viewer: `before` and `after` are the store either side of it. Pure but for
 * remembering which rolls have played and what this browser sent.
 */
export function effectsFor(ev: ProjectedEvent, before: ProjectedState, after: ProjectedState, meId: string, now = Date.now()): Effect[] {
  const out: Effect[] = [];
  if (ev.kind === 'dm.event') {
    // The DM is sent the roll, then its record again with each amendment or defence or bonus supplied (issue 1438).
    const b = ev.body;
    const roll = b.kind === 'roll' ? b.roll : (b.kind === 'roll.amend' || b.kind === 'attack.ac' || (b.kind === 'attack.answer' && b.choice === undefined) || b.kind === 'save.bonus' || b.kind === 'save.rethrow' || b.kind === 'save.apply') ? b.record : undefined;
    if (roll?.attack) out.push(...dmAttack(roll, after));
    else if (roll?.save) out.push(...dmSave(roll, after));
  } else if (ev.kind === 'attack') out.push(...playerAttackFx(ev.attack, after, meId, now));
  else if (ev.kind === 'save') out.push(...playerSaveFx(ev.save, after, meId, now));
  out.push(...gained(before, after));
  return out;
}

// ── The bus between the table's state and the board, and the numbers the perf readout shows ──

const playHandlers = new Set<(fx: Effect[]) => void>();
let activeCount = 0;
let playedCount = 0;
export const effectsBus = {
  /** Effects to play: dropped when this person has turned effects off. */
  play(fx: Effect[]): void {
    if (!fx.length || !effectsSetting.get()) return;
    playedCount += fx.length;
    for (const h of playHandlers) h(fx);
  },
  onPlay(h: (fx: Effect[]) => void): () => void { playHandlers.add(h); return () => { playHandlers.delete(h); }; },
  /** How many are playing on the board now; the board says, the readout reads. */
  setActive(n: number): void { activeCount = n; },
  stats(): { active: number; played: number } { return { active: activeCount, played: playedCount }; },
};

// ── Drawing ──────────────────────────────────────────────────────────────

/** The squares of an area, one rectangle a row, in board pixels: exactly what the area layer shades. */
export function burstRects(area: Area, grid: Grid, extent: { width: number; height: number }): Array<{ x: number; y: number; width: number; height: number }> {
  const { cols, rows } = boardSquares(grid, extent);
  return spanRects(areaSpans(area, cols, rows), grid, extent);
}

/** An effect as the board holds it while it plays: when it started, and a burst's rectangles worked out once. */
export interface Playing { fx: Effect; started: number; rects?: Array<{ x: number; y: number; width: number; height: number }>; centre?: Pt; reach?: number }

/** The few drawing calls the effects use (Pixi's Graphics has them all). */
export interface Pen {
  clear(): unknown;
  circle(x: number, y: number, r: number): Pen;
  rect(x: number, y: number, w: number, h: number): Pen;
  moveTo(x: number, y: number): Pen;
  lineTo(x: number, y: number): Pen;
  fill(style: { color: number; alpha: number }): Pen;
  stroke(style: { color: number; width: number; alpha: number }): Pen;
}

export function startPlaying(fx: Effect, now: number, grid: Grid, extent: { width: number; height: number }): Playing {
  if (fx.kind !== 'burst') return { fx, started: now };
  const rects = burstRects(fx.area, grid, extent);
  if (!rects.length) return { fx, started: now, rects };
  const x0 = Math.min(...rects.map((r) => r.x)); const x1 = Math.max(...rects.map((r) => r.x + r.width));
  const y0 = Math.min(...rects.map((r) => r.y)); const y1 = Math.max(...rects.map((r) => r.y + r.height));
  const centre = { x: (x0 + x1) / 2, y: (y0 + y1) / 2 };
  return { fx, started: now, rects, centre, reach: Math.hypot(x1 - x0, y1 - y0) / 2 };
}

const HIT = 0xef4444; const CRIT = 0xfacc15; const MISS = 0xd6d3d1; const STREAK = 0xfde68a; const FIRE = 0xf97316; const PULSE = 0x38bdf8;

/**
 * Draw the effects still playing at `now` and drop the ones that ended. Answers what is left: when it is empty the
 * layer has been cleared, and the board stops drawing per frame (its dirty flag goes back to being the only reason).
 */
export function drawEffects(g: Pen, list: Playing[], now: number, zoom: number): Playing[] {
  g.clear();
  const alive = list.filter((p) => now - p.started < Math.min(MAX_MS, EFFECT_MS[p.fx.kind]));
  for (const p of alive) {
    const k = (now - p.started) / EFFECT_MS[p.fx.kind];
    const fx = p.fx;
    if (fx.kind === 'attack') {
      // The first third: the streak runs from the attacker to the target. Then the outcome at the target.
      if (fx.from && k < 0.45) {
        const s = Math.min(1, k / 0.3); const tail = Math.max(0, (k - 0.2) / 0.25);
        const ax = fx.from.x + (fx.to.x - fx.from.x) * tail; const ay = fx.from.y + (fx.to.y - fx.from.y) * tail;
        g.moveTo(ax, ay).lineTo(fx.from.x + (fx.to.x - fx.from.x) * s, fx.from.y + (fx.to.y - fx.from.y) * s).stroke({ color: STREAK, width: 4 / zoom, alpha: 0.9 });
      }
      const t = fx.from ? (k - 0.3) / 0.7 : k;
      if (t >= 0) {
        if (fx.result === 'hit' || fx.result === 'critical') {
          const c = fx.result === 'critical' ? CRIT : HIT;
          g.circle(fx.to.x, fx.to.y, (10 + 30 * t) / zoom).fill({ color: c, alpha: 0.5 * (1 - t) });
          g.circle(fx.to.x, fx.to.y, (14 + 40 * t) / zoom).stroke({ color: c, width: 3 / zoom, alpha: 1 - t });
        } else if (fx.result === 'miss') {
          for (let n = 0; n < 3; n += 1) g.circle(fx.to.x + ((n - 1) * 12) / zoom, fx.to.y - (6 + 18 * t) / zoom, (6 + 8 * t) / zoom).fill({ color: MISS, alpha: 0.5 * (1 - t) });
        }
      }
    } else if (fx.kind === 'burst' && p.rects && p.centre && p.reach !== undefined) {
      // The fill spreads from the area's middle to its edge in the first half, then fades.
      const grow = Math.min(1, k / 0.5); const fade = k < 0.5 ? 0.55 : 0.55 * (1 - (k - 0.5) / 0.5);
      const r = p.reach * grow;
      for (const q of p.rects) {
        const dy = Math.max(0, Math.abs(q.y + q.height / 2 - p.centre.y) - q.height / 2);
        if (dy > r) continue;
        const half = Math.sqrt(r * r - dy * dy);
        const x0 = Math.max(q.x, p.centre.x - half); const x1 = Math.min(q.x + q.width, p.centre.x + half);
        if (x1 > x0) g.rect(x0, q.y, x1 - x0, q.height).fill({ color: FIRE, alpha: fade });
      }
    } else if (fx.kind === 'pulse') {
      g.circle(fx.at.x, fx.at.y, (12 + 26 * k) / zoom).stroke({ color: PULSE, width: 3 / zoom, alpha: 1 - k });
    }
  }
  return alive;
}
